import { useEffect, useRef } from 'react';

/*
 * Outside-pointer and Escape dismissal for popovers.
 *
 * Positioning is deliberately NOT part of this. The popovers in this app anchor themselves in
 * genuinely different ways — below the trigger (useMenu), above it (CreditsDisplay), to its
 * right or viewport-centred on mobile (SidebarDropdown) — and trying to force them through a
 * single positioning API is exactly why each one ended up hand-rolling the whole thing,
 * dismissal included. Dismissal is the part they all share, so that is the part that is shared.
 *
 * `refs` lists every element that counts as "inside". A portalled panel lives outside the
 * trigger's DOM subtree, so it must be passed separately from the trigger.
 *
 * pointerdown, not mousedown: it covers mouse, touch and pen alike. Under mousedown a touch
 * user waited on the browser's synthesized mouse event — or never got one, and the panel
 * simply stayed open.
 */
export const useDismissOnOutside = (isOpen, onDismiss, refs = []) => {
  // Held in refs so the listeners don't re-bind on every render: `refs` is a fresh array
  // literal each time, and callers rarely memoise onDismiss.
  const refsRef = useRef(refs);
  refsRef.current = refs;
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    if (!isOpen) return undefined;

    const isInside = (target) => refsRef.current.some((ref) => ref?.current?.contains(target));
    const onPointerDown = (e) => { if (!isInside(e.target)) onDismissRef.current(); };
    const onKeyDown = (e) => { if (e.key === 'Escape') onDismissRef.current(); };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);
};
